import React, { useState, useEffect } from "react";
import { makeRequest2 } from "../../../utils/makeRequest";
import { toastify } from "../../../utils/toast";
import { Button } from "primereact/button";
import { ADD_INVOICE_SCHEDULE_URL, GET_INVOICE_SCHEDULE_URL, EDIT_INVOICE_SCHEDULE_URL } from '../../../utils/urls';

const InvoiceScheduleTab = ({ facilityId }) => {
  const [invoiceType, setInvoiceType] = useState("");
  const [nextInvoiceDate, setNextInvoiceDate] = useState("");
  const [isAutomatic, setIsAutomatic] = useState(true);
  const [fetchedSchedules, setFetchedSchedules] = useState([]);
  
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);

  // Invoice types dropdown options
  const invoiceTypes = [
    { value: "LEASE", label: "Lease" },
    { value: "LEVY", label: "Levy" },
    { value: "PROPERTY-MANAGEMENT", label: "Property Management" },
    { value: "WATER", label: "Water" },
    { value: "ELECTRICITY", label: "Electricity" }
  ];

  const fetchSchedules = async () => {
    try {
      const response = await makeRequest2(
        `${GET_INVOICE_SCHEDULE_URL}/${facilityId}`,
        "GET"
      );
      if (response.success && response.data) {
        const scheduleData = response.data.data || response.data;
        const schedulesArray = Array.isArray(scheduleData)
          ? scheduleData
          : [scheduleData];
        setFetchedSchedules(schedulesArray);
      }
    } catch (error) {
      console.error("Error fetching invoice schedules:", error);
      toastify("Error fetching invoice schedules", "error");
    }
  };

  const handleScheduleSubmit = async () => {
    if (!invoiceType.trim()) {
      toastify("Please select an invoice type", "error");
      return;
    }

    if (!nextInvoiceDate) {
      toastify("Please select next invoice date", "error");
      return;
    }

    try {
      const data = {
        invoiceType: invoiceType.trim(),
        nextInvoiceDate: new Date(nextInvoiceDate).toISOString(),
        isAutomatic: isAutomatic
      };

      const response = await makeRequest2(
        `${ADD_INVOICE_SCHEDULE_URL}/${facilityId}`,
        "POST",
        data
      );

      if (response?.success === true || response?.message === "Invoice schedule added successfully") {
        toastify("Invoice schedule added successfully", "success");
        await fetchSchedules();
        resetForm();
      } else {
        const errorMessage = response?.message || response?.error || "Operation failed";
        toastify(errorMessage, "error");
        await fetchSchedules();
      }
    } catch (error) {
      console.error("Submit error:", error);
      toastify(error.message || "Error submitting invoice schedule", "error");
      await fetchSchedules();
    }
  };

  const resetForm = () => {
    setInvoiceType("");
    setNextInvoiceDate("");
    setIsAutomatic(true);
  };

  const handleEditSubmit = async () => {
    if (editingSchedule == null) return;

    if (!editingSchedule.invoiceType.trim()) {
      toastify("Please select an invoice type", "error");
      return;
    }

    if (!editingSchedule.nextInvoiceDate) {
      toastify("Please select next invoice date", "error");
      return;
    }

    try {
      const data = {
        invoiceType: editingSchedule.invoiceType.trim(),
        nextInvoiceDate: new Date(editingSchedule.nextInvoiceDate).toISOString(),
        isAutomatic: editingSchedule.isAutomatic
      };

      const response = await makeRequest2(
        `${EDIT_INVOICE_SCHEDULE_URL}/${editingSchedule._id}`,
        "PUT",
        data
      );
      
      if (response.success || response?.message === "Invoice schedule updated successfully") {
        toastify("Invoice schedule updated successfully", "success");
        await fetchSchedules();
        setShowEditModal(false);
        setEditingSchedule(null);
      } else {
        toastify(response.message || "Failed to update invoice schedule", "error");
      }
    } catch (error) {
      console.error("Edit error:", error);
      toastify("Error updating invoice schedule", "error");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getInvoiceTypeLabel = (type) => {
    const found = invoiceTypes.find(item => item.value === type);
    return found ? found.label : type;
  };

  // Format date for input field (YYYY-MM-DD)
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  useEffect(() => {
    if (facilityId) {
      fetchSchedules();
    }
  }, [facilityId]);

  return (
    <div className="row p-3">
      {/* Add Invoice Schedule Section */}
      <div className="col-lg-6">
        <div className="card">
          <div className="card-body">
            <h5>Add Invoice Schedule</h5>

            <div className="mt-2">
              <label>Invoice Type:</label>
              <select
                required
                className="form-control mt-2"
                value={invoiceType}
                onChange={(e) => setInvoiceType(e.target.value)}
              >
                <option value="">Select invoice type</option>
                {invoiceTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
              <small className="text-muted">
                Select the type of invoice to schedule
              </small>
            </div>

            <div className="mt-3">
              <label>Next Invoice Date:</label>
              <input
                required
                className="form-control mt-2"
                type="date"
                value={nextInvoiceDate}
                onChange={(e) => setNextInvoiceDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
              <small className="text-muted">
                Select when the next invoice should be generated
              </small>
            </div>

            <div className="mt-3">
              <label className="d-block mb-2">Invoice Generation Mode:</label>
              <div className="d-flex gap-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="invoiceMode"
                    id="automaticMode"
                    checked={isAutomatic === true}
                    onChange={() => setIsAutomatic(true)}
                  />
                  <label className="form-check-label" htmlFor="automaticMode">
                    <i className="ti ti-refresh me-1"></i>
                    Automatic
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="invoiceMode"
                    id="manualMode"
                    checked={isAutomatic === false}
                    onChange={() => setIsAutomatic(false)}
                  />
                  <label className="form-check-label" htmlFor="manualMode">
                    <i className="ti ti-hand-click me-1"></i>
                    Manual
                  </label>
                </div>
              </div>
              <small className="text-muted">
                {isAutomatic 
                  ? "Invoices will be generated automatically on the scheduled date" 
                  : "Invoices will require manual generation"}
              </small>
            </div>

            <div className="mt-4" style={{ textAlign: "right" }}>
              <button 
                className="btn btn-primary" 
                onClick={handleScheduleSubmit}
                disabled={!invoiceType.trim() || !nextInvoiceDate}
              >
                Add Schedule
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Current Invoice Schedules Section */}
      <div className="col-lg-6">
        <div className="card">
          <div className="card-body">
            <h5 className="mb-4">Current Invoice Schedules</h5>
            <div className="table-responsive">
              <table className="table align-middle">
                <thead>
                  <tr>
                    <th scope="col">INVOICE TYPE</th>
                    <th scope="col">NEXT INVOICE</th>
                    <th scope="col">MODE</th>
                    <th scope="col">CREATED</th>
                    <th scope="col" className="text-end">ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {fetchedSchedules.length > 0 ? (
                    fetchedSchedules.map((schedule) => (
                      <tr key={schedule._id}>
                        <td>
                          <div className="d-flex align-items-center">
                            <div className="me-2">
                              <i className="ti ti-calendar-event text-primary" style={{ fontSize: "18px" }}></i>
                            </div>
                            <div>
                              <strong>{getInvoiceTypeLabel(schedule.invoiceType)}</strong>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="badge bg-info-subtle text-info">
                            {formatDate(schedule.nextInvoiceDate)}
                          </span>
                        </td>
                        <td>
                          {schedule.isAutomatic !== undefined ? (
                            <span className={`badge ${schedule.isAutomatic ? 'bg-success-subtle text-success' : 'bg-warning-subtle text-warning'}`}>
                              <i className={`ti ${schedule.isAutomatic ? 'ti-refresh' : 'ti-hand-click'} me-1`}></i>
                              {schedule.isAutomatic ? 'Automatic' : 'Manual'}
                            </span>
                          ) : (
                            <span className="badge bg-secondary-subtle text-secondary">N/A</span>
                          )}
                        </td>
                        <td>
                          {schedule.createdAt ? (
                            formatDate(schedule.createdAt)
                          ) : (
                            'N/A'
                          )}
                        </td>
                        <td className="text-end">
                          <Button
                            icon="ti ti-pencil"
                            className="p-button-rounded p-button-info"
                            onClick={() => {
                              setEditingSchedule({
                                ...schedule,
                                nextInvoiceDate: formatDateForInput(schedule.nextInvoiceDate),
                                isAutomatic: schedule.isAutomatic !== undefined ? schedule.isAutomatic : true
                              });
                              setShowEditModal(true);
                            }}
                            tooltip="Edit Schedule"
                          />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center py-4">
                        <div className="text-muted">
                          <i className="ti ti-calendar-off mb-2" style={{ fontSize: "24px" }}></i>
                          <p className="mb-0">No invoice schedules available</p>
                          <small>Add your first invoice schedule to get started</small>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Invoice Schedule Modal */}
      {showEditModal && editingSchedule && (
        <>
          <div className="modal show fade" style={{ display: "block" }} tabIndex="-1">
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="ti ti-pencil me-2"></i>
                    Edit Invoice Schedule
                  </h5>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setShowEditModal(false)}
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Invoice Type</label>
                    <select
                      className="form-control"
                      value={editingSchedule.invoiceType || ""}
                      onChange={(e) =>
                        setEditingSchedule({
                          ...editingSchedule,
                          invoiceType: e.target.value
                        })
                      }
                    >
                      <option value="">Select invoice type</option>
                      {invoiceTypes.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                    <small className="text-muted">
                      Select the type of invoice to schedule
                    </small>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Next Invoice Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={editingSchedule.nextInvoiceDate || ""}
                      onChange={(e) =>
                        setEditingSchedule({
                          ...editingSchedule,
                          nextInvoiceDate: e.target.value
                        })
                      }
                      min={new Date().toISOString().split('T')[0]}
                    />
                    <small className="text-muted">
                      Select when the next invoice should be generated
                    </small>
                  </div>

                  <div className="mb-3">
                    <label className="form-label d-block">Invoice Generation Mode</label>
                    <div className="d-flex gap-3">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="editInvoiceMode"
                          id="editAutomaticMode"
                          checked={editingSchedule.isAutomatic === true}
                          onChange={() =>
                            setEditingSchedule({
                              ...editingSchedule,
                              isAutomatic: true
                            })
                          }
                        />
                        <label className="form-check-label" htmlFor="editAutomaticMode">
                          <i className="ti ti-refresh me-1"></i>
                          Automatic
                        </label>
                      </div>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="editInvoiceMode"
                          id="editManualMode"
                          checked={editingSchedule.isAutomatic === false}
                          onChange={() =>
                            setEditingSchedule({
                              ...editingSchedule,
                              isAutomatic: false
                            })
                          }
                        />
                        <label className="form-check-label" htmlFor="editManualMode">
                          <i className="ti ti-hand-click me-1"></i>
                          Manual
                        </label>
                      </div>
                    </div>
                    <small className="text-muted">
                      {editingSchedule.isAutomatic 
                        ? "Invoices will be generated automatically on the scheduled date" 
                        : "Invoices will require manual generation"}
                    </small>
                  </div>
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => setShowEditModal(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-primary" 
                    onClick={handleEditSubmit}
                    disabled={!editingSchedule.invoiceType?.trim() || !editingSchedule.nextInvoiceDate}
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          </div>
          {/* Backdrop */}
          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </div>
  );
};

export default InvoiceScheduleTab;